// OpenRouter API Service
import { getModelConfig, MODEL_MODES } from './llmModels.js';

/**
 * OpenRouter API configuration
 */
const OPENROUTER_CONFIG = {
  baseURL: 'https://openrouter.ai/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'HTTP-Referer': import.meta.env.VITE_SITE_URL || window.location.origin, // Optional: for rankings
    'X-Title': import.meta.env.VITE_SITE_NAME || 'Cara AI Assistant' // Optional: for rankings
  }
};

/**
 * OpenRouter API Client
 */
export class OpenRouterAPI {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.baseURL = OPENROUTER_CONFIG.baseURL;
    this.maxRetries = 3;
    this.baseDelay = 1000; // 1 second
  }

  /**
   * Sleep utility for retry delays
   * @param {number} ms - Milliseconds to sleep
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if error is retryable
   * @param {Error} error - Error to check
   * @returns {boolean} Whether error is retryable
   */
  isRetryableError(error) {
    // Timeout errors are retryable
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      return true;
    }
    
    // Network errors are retryable
    if (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('NetworkError')) {
      return true;
    }
    
    // JSON parsing errors might be retryable (could be incomplete response)
    if (error.message.includes('Invalid JSON response') || error.message.includes('Unexpected end of JSON input')) {
      return true;
    }
    
    // Rate limiting is retryable
    if (error.message.includes('429') || error.message.includes('rate limit')) {
      return true;
    }
    
    // Server errors (5xx) are retryable
    if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
      return true;
    }
    
    return false;
  }

  /**
   * Get headers for API requests
   * @returns {Object} Request headers
   */
  getHeaders() {
    return {
      ...OPENROUTER_CONFIG.headers,
      'Authorization': `Bearer ${this.apiKey}`
    };
  }

  /**
   * Create chat completion request
   * @param {Object} options - Request options
   * @param {string} options.mode - Model mode (standard or thinking)
   * @param {Array} options.messages - Chat messages
   * @param {boolean} options.stream - Enable streaming
   * @param {string} options.searchType - Search type (web or academic) for token optimization
   * @param {Object} options.additionalParams - Additional parameters
   * @returns {Promise} API response
   */
  async createChatCompletion({
    mode = MODEL_MODES.STANDARD,
    messages = [],
    stream = false,
    searchType = 'web',
    additionalParams = {}
  }) {
    console.log('🔍 OpenRouter API - Mode received:', mode);
    console.log('🔍 OpenRouter API - Search type received:', searchType);
    const modelConfig = getModelConfig(mode, searchType);
    console.log('🤖 OpenRouter API - Model config:', {
      id: modelConfig.id,
      name: modelConfig.name,
      maxTokens: modelConfig.maxTokens,
      searchType: searchType,
      reasoning: modelConfig.reasoning
    });
    
    const requestBody = {
      model: modelConfig.id,
      messages,
      max_tokens: modelConfig.maxTokens, // This now includes search-type specific token count
      stream,
      ...additionalParams
    };
    
    console.log('📊 OpenRouter API - Request tokens configured:', {
      searchType: searchType,
      maxTokens: modelConfig.maxTokens,
      mode: mode
    });

    // Add reasoning configuration for thinking mode
    if (mode === MODEL_MODES.THINKING && modelConfig.reasoning) {
      requestBody.reasoning = {
        enabled: true,
        max_tokens: modelConfig.reasoning.maxTokens
      };
    }

    // Retry logic with exponential backoff
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        console.log(`🔄 API Request attempt ${attempt}/${this.maxRetries}`);
        
        // Create AbortController for timeout handling
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
        
        const response = await fetch(`${this.baseURL}/chat/completions`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(requestBody),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          let errorData = {};
          try {
            const errorText = await response.text();
            if (errorText && errorText.trim() !== '') {
              errorData = JSON.parse(errorText);
            }
          } catch (parseError) {
            console.warn('Failed to parse error response:', parseError.message);
          }
          
          const error = new Error(
            `OpenRouter API Error: ${response.status} - ${errorData.error?.message || response.statusText}`
          );
          
          // Don't retry on client errors (4xx)
          if (response.status >= 400 && response.status < 500) {
            throw error;
          }
          
          // Retry on server errors (5xx)
          if (attempt === this.maxRetries) {
            throw error;
          }
          
          console.warn(`⚠️ Server error ${response.status}, retrying...`);
          continue;
        }

        // Success - return response
        console.log(`✅ API Request successful on attempt ${attempt}`);
        
        if (stream) {
          return response; // Return response for streaming
        }

        // Validate response before parsing JSON
        const responseText = await response.text();
        if (!responseText || responseText.trim() === '') {
          throw new Error('Empty response received from API');
        }
        
        try {
          const data = JSON.parse(responseText);
          return data;
        } catch (jsonError) {
          console.error('❌ Failed to parse JSON response:', {
            error: jsonError.message,
            responseLength: responseText.length,
            responsePreview: responseText.substring(0, 200)
          });
          throw new Error(`Invalid JSON response: ${jsonError.message}`);
        }
        
      } catch (error) {
        console.error(`❌ API Request failed on attempt ${attempt}:`, error.message);
        
        // Handle specific error types
        if (error.name === 'AbortError') {
          console.error('⏰ Request timeout after 60 seconds');
          error.message = 'Request timeout - please try again';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          console.error('🌐 Network connection error');
          error.message = 'Network connection failed - please check your internet connection';
        } else if (error.message.includes('Invalid JSON response')) {
          console.error('📄 Invalid response format');
        }
        
        // Check if this is the last attempt
        if (attempt === this.maxRetries) {
          console.error('🚫 All retry attempts exhausted');
          throw error;
        }
        
        // Check if error is retryable
        if (!this.isRetryableError(error)) {
          console.error('🚫 Non-retryable error, aborting');
          throw error;
        }
        
        // Calculate delay with exponential backoff
        const delay = this.baseDelay * Math.pow(2, attempt - 1);
        console.log(`⏳ Retrying in ${delay}ms...`);
        
        await this.sleep(delay);
      }
    }
    
    // This should never be reached, but just in case
    throw new Error('Unexpected error in retry logic');
  }

  /**
   * Handle streaming response
   * @param {Response} response - Fetch response object
   * @param {Function} onChunk - Callback for each chunk
   * @param {Function} onComplete - Callback when complete
   * @param {Function} onError - Callback for errors
   */
  async handleStreamingResponse(response, onChunk, onComplete, onError) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          if (onComplete) onComplete();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.trim() === '') continue;
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              if (onComplete) onComplete();
              return;
            }
            
            try {
              const parsed = JSON.parse(data);
              if (onChunk) onChunk(parsed);
            } catch (e) {
              console.warn('Failed to parse streaming chunk:', data);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      if (onError) onError(error);
    }
  }

  /**
   * Test API connection
   * @returns {Promise<boolean>} Connection status
   */
  async testConnection() {
    try {
      const response = await this.createChatCompletion({
        mode: MODEL_MODES.STANDARD,
        messages: [{ role: 'user', content: 'Hello' }],
        searchType: 'web', // Use web search type for connection test
        additionalParams: { max_tokens: 10 }
      });
      return !!response;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }
}

/**
 * Create OpenRouter API instance
 * @param {string} apiKey - OpenRouter API key
 * @returns {OpenRouterAPI} API instance
 */
export const createOpenRouterAPI = (apiKey) => {
  if (!apiKey) {
    throw new Error('OpenRouter API key is required');
  }
  return new OpenRouterAPI(apiKey);
};

/**
 * Default export for convenience
 */
export default OpenRouterAPI;